import React from "react";
import { Alert, Box, IconButton, Stack, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { relativeDate, surfaceSx } from "./ownerCenterUi";

const FOLLOW_UP_KINDS = new Set(["follow_up", "promise", "reminder"]);

function idOf(value) { return String(value?.id || value?._id || value || ""); }
function localTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("es-AR", { hour: "2-digit", minute: "2-digit" }).format(date);
}

function Row({ icon: Icon, title, meta, done, busy, onDone }) {
  return (
    <Stack direction="row" spacing={.8} alignItems="center" sx={{ py: .65, minHeight: 54 }}>
      <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: "action.hover", display: "grid", placeItems: "center", flex: "0 0 auto" }}><Icon sx={{ fontSize: 18 }} /></Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontWeight: 950, fontSize: { xs: 13.2, sm: 14 } }}>{title}</Typography>
        {meta ? <Typography noWrap sx={{ color: "text.secondary", fontWeight: 750, fontSize: 11 }}>{meta}</Typography> : null}
      </Box>
      <IconButton onClick={onDone} disabled={busy || done} aria-label={`Completar ${title}`} sx={{ width: 38, height: 38, border: "1px solid", borderColor: done ? "success.main" : "divider", color: done ? "success.main" : "text.secondary" }}>
        <CheckRoundedIcon />
      </IconButton>
    </Stack>
  );
}

export default function OwnerDailyPlan({ calendar, allCommitments = [], onOpenCalendar, onChanged }) {
  const [updating, setUpdating] = React.useState("");
  const [error, setError] = React.useState("");
  const day = calendar?.day || {};
  const date = calendar?.selectedDate || "";
  const routines = (day.routines || []).filter((item) => !item.completed);
  const commitments = day.commitments || [];
  const tasks = commitments.filter((item) => item.status === "open" && !FOLLOW_UP_KINDS.has(item.kind));
  const datedFollowUps = commitments.filter((item) => item.status === "open" && FOLLOW_UP_KINDS.has(item.kind));
  const datedIds = new Set(datedFollowUps.map(idOf));
  const floatingFollowUps = allCommitments.filter((item) => item.status !== "done" && FOLLOW_UP_KINDS.has(item.kind) && !datedIds.has(idOf(item))).slice(0, 2);

  const run = async (key, task, fallback) => {
    setUpdating(key); setError("");
    try { await task(); await onChanged?.(); }
    catch (err) { setError(formatApiError(err, fallback)); }
    finally { setUpdating(""); }
  };

  const rows = [
    ...tasks.map((item) => ({ type: "task", item, icon: TaskAltRoundedIcon, title: item.title, meta: item.scheduledStartAt ? localTime(item.scheduledStartAt) : relativeDate(item.dueAt) })),
    ...datedFollowUps.map((item) => ({ type: "follow", item, icon: NotificationsActiveRoundedIcon, title: item.title, meta: relativeDate(item.scheduledStartAt || item.dueAt) })),
    ...floatingFollowUps.map((item) => ({ type: "follow", item, icon: NotificationsActiveRoundedIcon, title: item.title, meta: "Seguimiento" })),
    ...routines.map((item) => ({ type: "routine", item, icon: ReplayRoundedIcon, title: item.name, meta: item.startTime ? `${item.startTime}–${item.endTime}` : "Rutina" })),
  ].slice(0, 6);

  const complete = (row) => {
    const id = idOf(row.item);
    if (!id) return;
    if (row.type === "routine") return run(`routine:${id}`, () => ownerApi.setRoutineCheckin(id, date, true), "No pude marcar la rutina.");
    return run(`commitment:${id}`, () => ownerApi.updateCommitment(id, { status: "done" }), "No pude completar esto.");
  };

  return (
    <Box data-owner-jarvis-section="today-agenda" sx={{ ...surfaceSx, p: { xs: 1.35, sm: 1.7, md: 2 }, minHeight: { md: 330 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography sx={{ color: "primary.main", fontWeight: 1000, fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em" }}>Tu día</Typography>
          <Typography sx={{ mt: .2, fontWeight: 1000, fontSize: { xs: 20, sm: 23 }, letterSpacing: "-.03em" }}>{rows.length ? "Lo que sigue" : "Día despejado"}</Typography>
        </Box>
        <IconButton onClick={onOpenCalendar} aria-label="Abrir agenda" sx={{ width: 44, height: 44, border: "1px solid", borderColor: "divider" }}><CalendarMonthRoundedIcon /></IconButton>
      </Stack>

      {error ? <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert> : null}

      {rows.length ? (
        <Stack sx={{ mt: .8 }}>
          {rows.map((row) => <Row key={`${row.type}:${idOf(row.item)}`} icon={row.icon} title={row.title} meta={row.meta} busy={updating === `${row.type === "routine" ? "routine" : "commitment"}:${idOf(row.item)}`} onDone={() => complete(row)} />)}
        </Stack>
      ) : (
        <Box sx={{ minHeight: 210, display: "grid", placeItems: "center", textAlign: "center" }}>
          <Box><TaskAltRoundedIcon color="success" sx={{ fontSize: 44 }} /><Typography sx={{ mt: .7, fontWeight: 950 }}>Nada urgente</Typography><Typography sx={{ mt: .25, color: "text.secondary", fontWeight: 700, fontSize: 12 }}>No llenamos huecos porque sí.</Typography></Box>
        </Box>
      )}

      <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mt: .5, cursor: "pointer" }} onClick={onOpenCalendar}>
        <Typography sx={{ color: "text.secondary", fontWeight: 900, fontSize: 11.5 }}>Agenda completa</Typography><ArrowForwardRoundedIcon sx={{ ml: .25, fontSize: 17, color: "text.secondary" }} />
      </Stack>
    </Box>
  );
}
